import {peakConfirm} from "../../PeakDialog.js";
import {ZIDX, ZIDXPage} from "../../standalone/standaloneLibrary.js";

interface ChatSessionItem {
    sessionId: string;
    title: string;
    updatedAt: string;
}

interface ChatMessageItem {
    messageId: number;
    sequence: number;
    role: 'user' | 'assistant' | 'system' | 'tool' | 'summary';
    content: string | null;
    toolName: string | null;
    toolInput: string | null;
    toolOutput: string | null;
    toolCallId: string | null;
    createdAt: string;
}

interface SendResponse {
    success: boolean;
    sessionId: string;
    assistantMessage?: string;
    summarized?: boolean;
    errorMessage?: string;
}

const INJECTION_PATTERNS = [
    /ignore\s+(all\s+)?(previous|prior|above)\s+instructions?/i,
    /disregard\s+(all\s+)?(previous|prior|above)\s+instructions?/i,
    /override\s+(your\s+)?(instructions?|rules?|guidelines?)/i,
    /you\s+are\s+now/i,
    /act\s+as\s+(?!.*real\s+estate)/i,
    /jailbreak/i,
    /roleplay\s+as/i,
    /bypass\s+(your\s+)?(safety|filter|restriction|rule)/i,
];


export class ZIDXAiChatBox implements ZIDXPage {
    zidx: ZIDX;
    sessionId: string | null = null;
    persisted: boolean = false;
    sequence: number = 1;
    sending: boolean = false;
    lastSendTime: number = 0;

    // DOM elements
    historyList: HTMLElement | null = null;
    messagesContainer: HTMLElement | null = null;
    chatInput: HTMLTextAreaElement | null = null;
    sendButton: HTMLElement | null = null;
    newChatButton: HTMLElement | null = null;

    constructor(zidx: ZIDX) {
        this.zidx = zidx;
    }

    getPageClass(): string {
        return "zidxAiChatBoxContainer";
    }

    render(): void {
        this.historyList = document.getElementById("AiChatHistoryList");
        this.messagesContainer = document.getElementById("AiChatMessages");
        this.chatInput = document.getElementById("AiChatInput") as HTMLTextAreaElement;
        this.sendButton = document.getElementById("AiChatSendButton");
        this.newChatButton = document.getElementById("AiChatNewButton");

        if (!this.messagesContainer || !this.chatInput || !this.sendButton) return;

        this.sendButton.addEventListener("click", () => this.handleSend());
        this.chatInput.addEventListener("keydown", (e: KeyboardEvent) => {
            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                this.handleSend();
            }
        });

        if (this.newChatButton) {
            this.newChatButton.addEventListener("click", () => this.startNewChat());
        }

        this.loadHistory();
        this.startNewChat();
    }

    // ---------------------------------------------------------------
    // Session lifecycle
    // ---------------------------------------------------------------
    async startNewChat() {
        this.persisted = false;
        this.sequence = 1;
        if (this.messagesContainer) {
            this.messagesContainer.innerHTML = "";
        }

        try {
            const res = await fetch("/ai-chat/init", {
                method: "POST",
                credentials: "include",
            });
            const data = await res.json();
            if (data.success) {
                this.sessionId = data.sessionId;
            }
        } catch (e) {
            console.error("Failed to init chat session", e);
        }
    }

    async resumeSession(sessionId: string) {
        this.sessionId = sessionId;
        this.persisted = true;
        if (this.messagesContainer) {
            this.messagesContainer.innerHTML = "";
        }

        try {
            const res = await fetch("/ai-chat/resume?sessionId=" + encodeURIComponent(sessionId), {
                credentials: "include",
            });
            const data = await res.json();
            if (data.success && data.messages) {
                const messages: ChatMessageItem[] = data.messages;
                for (const msg of messages) {
                    if (msg.role === "user" || msg.role === "assistant") {
                        this.appendMessageToUI(msg.role, msg.content || "");
                    }
                }
                // Set sequence to continue after last message
                if (messages.length > 0) {
                    this.sequence = Math.max(...messages.map(m => m.sequence)) + 1;
                }
            }
        } catch (e) {
            console.error("Failed to resume session", e);
        }

        // Highlight active session in sidebar
        this.highlightActiveSession(sessionId);
    }

    // ---------------------------------------------------------------
    // Input validation (UX only — not a security boundary)
    // ---------------------------------------------------------------
    validateInput(content: string): { ok: boolean; message?: string } {
        if (content.length > 2000) {
            return { ok: false, message: "Message too long (max 2000 characters)." };
        }
        if (content.length < 3) {
            return { ok: false, message: "Message too short." };
        }
        for (const pattern of INJECTION_PATTERNS) {
            if (pattern.test(content)) {
                // Soft warning — server will enforce the real block
                return { ok: true, message: "I'm designed for real estate queries. Your message may not be processed as expected." };
            }
        }
        return { ok: true };
    }

    // ---------------------------------------------------------------
    // Send message
    // ---------------------------------------------------------------
    async handleSend() {
        if (this.sending || !this.chatInput || !this.sessionId) return;
        const content = this.chatInput.value.trim();
        if (!content) return;

        // Submit throttle (1-second minimum between sends)
        const now = Date.now();
        if (now - this.lastSendTime < 1000) return;

        // UX validation
        const validation = this.validateInput(content);
        if (!validation.ok) {
            this.appendMessageToUI("assistant", validation.message || "Invalid input.");
            return;
        }
        if (validation.message) {
            this.appendMessageToUI("assistant", validation.message);
        }

        this.lastSendTime = now;

        this.sending = true;
        this.chatInput.value = "";
        this.appendMessageToUI("user", content);
        this.setSendingState(true);

        try {
            const body: Record<string, unknown> = {
                sessionId: this.sessionId,
                content: content,
                sequence: this.sequence,
            };

            if (!this.persisted) {
                body.isNew = true;
            }

            const res = await fetch("/ai-chat/send", {
                method: "POST",
                credentials: "include",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify(body),
            });

            const data: SendResponse = await res.json();

            if (data.success) {
                this.persisted = true;

                if (data.assistantMessage) {
                    this.appendMessageToUI("assistant", data.assistantMessage);
                    // user message + assistant message (+ possible tool messages + summary)
                    this.sequence += 2;
                    if (data.summarized) {
                        this.sequence++;
                    }
                }

                // Refresh history sidebar to show new/updated session
                this.loadHistory();
            } else {
                this.appendMessageToUI("assistant", "Sorry, an error occurred: " + (data.errorMessage || "Unknown error"));
            }
        } catch (e) {
            console.error("Failed to send message", e);
            this.appendMessageToUI("assistant", "Sorry, a network error occurred. Please try again.");
        } finally {
            this.sending = false;
            this.setSendingState(false);
        }
    }

    // ---------------------------------------------------------------
    // Chat history sidebar
    // ---------------------------------------------------------------
    async loadHistory() {
        if (!this.historyList) return;

        try {
            const res = await fetch("/ai-chat/history?limit=20&offset=0", {
                credentials: "include",
            });
            const data = await res.json();

            if (data.success && data.sessions) {
                this.historyList.innerHTML = "";
                const sessions: ChatSessionItem[] = data.sessions;

                if (sessions.length === 0) {
                    this.historyList.innerHTML = "<p>No previous chats</p>";
                    return;
                }

                for (const session of sessions) {
                    const item = document.createElement("div");
                    item.className = "zidx-ai-chat-history-item";
                    item.dataset.sessionId = session.sessionId;

                    const title = document.createElement("span");
                    title.className = "zidx-ai-chat-history-title";
                    title.textContent = session.title || "Untitled Chat";
                    item.appendChild(title);

                    const deleteBtn = document.createElement("button");
                    deleteBtn.className = "zidx-ai-chat-history-delete";
                    deleteBtn.textContent = "×";
                    deleteBtn.addEventListener("click", (e) => {
                        e.stopPropagation();
                        this.deleteSession(session.sessionId);
                    });
                    item.appendChild(deleteBtn);

                    item.addEventListener("click", () => {
                        this.resumeSession(session.sessionId);
                    });

                    this.historyList.appendChild(item);
                }

                if (this.sessionId) {
                    this.highlightActiveSession(this.sessionId);
                }
            }
        } catch (e) {
            console.error("Failed to load chat history", e);
            if (this.historyList) {
                this.historyList.innerHTML = "<p>Failed to load history</p>";
            }
        }
    }

    async deleteSession(sessionId: string) {
        if (!(await peakConfirm("Delete this chat?"))) return;

        try {
            const res = await fetch("/ai-chat/delete", {
                method: "POST",
                credentials: "include",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({sessionId}),
            });
            const data = await res.json();

            if (data.success) {
                // If we deleted the active session, start a new one
                if (this.sessionId === sessionId) {
                    this.startNewChat();
                }
                this.loadHistory();
            }
        } catch (e) {
            console.error("Failed to delete session", e);
        }
    }

    // ---------------------------------------------------------------
    // UI helpers
    // ---------------------------------------------------------------
    appendMessageToUI(role: string, content: string): void {
        if (!this.messagesContainer) return;
        const msgDiv = document.createElement("div");
        msgDiv.className = "zidx-ai-chat-message zidx-ai-chat-message-" + role;

        const label = document.createElement("span");
        label.className = "zidx-ai-chat-message-role";
        label.textContent = role === "user" ? "You" : "AI";
        msgDiv.appendChild(label);

        const text = document.createElement("div");
        text.className = "zidx-ai-chat-message-content";
        text.textContent = content;
        msgDiv.appendChild(text);

        this.messagesContainer.appendChild(msgDiv);
        this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
    }

    setSendingState(sending: boolean): void {
        if (this.sendButton) {
            (this.sendButton as HTMLButtonElement).disabled = sending;
            this.sendButton.textContent = sending ? "Sending..." : "Send";
        }
        if (this.chatInput) {
            this.chatInput.disabled = sending;
        }
    }

    highlightActiveSession(sessionId: string): void {
        if (!this.historyList) return;
        const items = this.historyList.querySelectorAll(".zidx-ai-chat-history-item");
        items.forEach((item) => {
            const el = item as HTMLElement;
            if (el.dataset.sessionId === sessionId) {
                el.classList.add("zidx-ai-chat-history-active");
            } else {
                el.classList.remove("zidx-ai-chat-history-active");
            }
        });
    }
}